import { requirePayoutDetails } from './configHelpers';
import { isPrivateSellerListing } from './sellerType';
import { isManualVehicleSettlement } from './settlement';

/**
 * Whether a listing must have provider payout details before it can be
 * published.
 *
 * Private sellers are exempt. The listing-type flag that governs this is set
 * per listing type in hosted config, but "private seller" is a per-listing
 * field (publicData.sellerType), so the two do not line up -- turning the flag
 * off in Console would exempt dealers on the same listing type too. Hence the
 * distinction lives here.
 *
 * Removing the gate does not make an unconnected listing sellable through
 * Skip: a customer still cannot complete a purchase or booking with a provider
 * who has no payout details. What changes is where the seller finds that out.
 * Before, "Publish" silently opened the payout modal and the listing stayed a
 * draft with no standing explanation. Now the listing publishes, and the
 * seller's own listing page carries Sharetribe's existing warning that it is
 * not available for purchase until payout details are added.
 */

export { isPrivateSellerListing };

/**
 * @param {Object} listingTypeConfig the listing type's hosted config
 * @param {Object} listing the listing being published
 * @returns {boolean} true when publishing should be held until payout details
 *   are set
 */
export const requirePayoutDetailsToPublish = (listingTypeConfig, listing) =>
  requirePayoutDetails(listingTypeConfig) && !isPrivateSellerListing(listing);

/**
 * The whole publish-time decision, in one testable place: whether hitting
 * Publish should open the payout-details modal instead of publishing.
 *
 * @param {Object} params
 * @param {Object} params.listing
 * @param {Object} params.listingTypeConfig
 * @param {boolean} params.isInquiryProcess inquiry listings never take payment
 * @param {boolean} params.stripeConnected
 * @param {boolean} params.stripeRequirementsMissing past_due or currently_due
 * @returns {boolean} true to hold publishing and ask for payout details
 */
export const shouldAskForPayoutDetailsBeforePublish = ({
  listing,
  listingTypeConfig,
  isInquiryProcess,
  stripeConnected,
  stripeRequirementsMissing,
}) => {
  if (isInquiryProcess) return false;
  // Vehicle sales are settled off the platform (util/settlement), so there are
  // no checkout funds for payout details to receive. Asking for them would hold
  // a dealer's listing as a draft for a step that serves no purpose.
  if (isManualVehicleSettlement()) return false;
  if (!requirePayoutDetailsToPublish(listingTypeConfig, listing)) return false;
  return !stripeConnected || !!stripeRequirementsMissing;
};
